import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { BeoflowButton } from '@/components/BeoflowButton'
import { Sentry, getSentrySessionId } from '@/lib/sentry'
import { isStaleChunkError, isStaleChunkReloadInFlight, reloadOnceForStaleChunk } from '@/lib/lazyWithReload'
import { beoflowColors, typography } from '@/theme/muiTheme'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isStaleChunk: boolean
  errorMessage: string | null
  eventId: string | null
  sessionId: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isStaleChunk: false, errorMessage: null, eventId: null, sessionId: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // A stale-chunk error that reaches here means the import-promise guards were
    // bypassed (BEO-2381). Render a neutral spinner instead of the crash UI while
    // the reload is dispatched in componentDidCatch.
    return {
      hasError: true,
      isStaleChunk: isStaleChunkError(error),
      errorMessage: error.message,
      eventId: null,
      sessionId: getSentrySessionId(),
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isStaleChunkError(error)) {
      // Recover transparently — this is expected post-deploy churn, not a bug,
      // so don't report it to Sentry. reloadOnceForStaleChunk returns false once
      // the per-release reload budget is spent; unless that reload is still in
      // flight from this page life (BEO-3208), fall through to the crash UI so a
      // genuinely broken build is still surfaced.
      if (reloadOnceForStaleChunk() || isStaleChunkReloadInFlight()) return
      this.setState({ isStaleChunk: false })
    }
    const sessionId = getSentrySessionId()
    console.error('ErrorBoundary caught:', error, info.componentStack)
    const eventId = Sentry.captureException(error, {
      tags: { session_id: sessionId },
      contexts: {
        react: {
          componentStack: info.componentStack,
        },
      },
    })
    this.setState({ errorMessage: error.message, eventId, sessionId })
  }

  render() {
    if (this.state.hasError && this.state.isStaleChunk) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
          <CircularProgress size={32} color="info" />
        </Box>
      )
    }

    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            backgroundColor: beoflowColors.beoBlacker,
            color: beoflowColors.beoWhite,
            fontFamily: typography.fontFamilyMonospace,
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Something went wrong.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            An unexpected error occurred.
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.5, color: 'text.secondary', fontSize: 12, maxWidth: 560, textAlign: 'center', wordBreak: 'break-all' }}>
            {this.state.errorMessage && <Typography variant="caption">Error: {this.state.errorMessage}</Typography>}
            {this.state.eventId && <Typography variant="caption">Sentry event: {this.state.eventId}</Typography>}
            {this.state.sessionId && <Typography variant="caption">Session: {this.state.sessionId}</Typography>}
          </Box>
          <BeoflowButton
            variant="outlineSecondary"
            onClick={() => {
              this.setState({ hasError: false })
              window.location.reload()
            }}
          >
            Reload
          </BeoflowButton>
        </Box>
      )
    }

    return this.props.children
  }
}
